import { Button, Flex, Typography, message } from 'antd';
import { FetchingErrorState } from '@shared/ui/fetching-error-state/fetching-error-state';
import { LoadingState } from '@shared/ui/loading-state/loading-state';
import styles from './knowledge-base.module.scss';
import { useGetKnowledgeListKnowledgeListGetQuery } from '@shared/api';
import { KnowledgeBaseTable } from './ui/knowledge-base-table';
import { useToggle } from '@shared/hooks/use-toggle';
import { KnowledgeBaseCreateModal } from './ui/ui/create-modal/create-modal';
import { authSelectors } from '@shared/redux/ducks/auth';
import { useAppSelector } from '@shared/redux';
import { Trans, useTranslation } from 'react-i18next';
import { useDeleteKnowledgeBase } from './hooks/use-delete-knowledge-base';
import { useGlobalConfirmModalActions } from '@shared/redux/ducks/global-confirm-modal';
import { PageHeader } from '@shared/ui/page-header';
import { KnowledgeBaseEmptyState } from './ui/empty-state/knowledge-base-empty-state';
import { useLazyDownloadKnowledgeBaseFileQuery } from '@shared/api/api-blob';
import { DateService } from '@shared/services/date';
import { useEffect, useState } from 'react';

export const KnowledgeBase = () => {
  const { Text: AntdText } = Typography;
  const { t } = useTranslation('translation', { keyPrefix: 'knowledgeBase' });
  const [selectedKnowledgeBaseIds, setSelectedKnowledgeBaseIds] = useState<number[]>([]);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  const {
    state: isCreateKnowledgeBaseModalOpen,
    setTrue: openCreateKnowledgeBaseModal,
    setFalse: closeCreateKnowledgeBaseModal,
  } = useToggle();

  const currentOrganization = useAppSelector(authSelectors.selectCurrentOrganizationInfo);

  const { data, isLoading, isError, refetch } = useGetKnowledgeListKnowledgeListGetQuery(
    {
      organizationId: currentOrganization.id,
    },
    {
      refetchOnMountOrArgChange: true,
    },
  );

  const { openConfirmModal } = useGlobalConfirmModalActions();
  const { deleteKnowledgeBase } = useDeleteKnowledgeBase();

  useEffect(() => {
    const availableKnowledgeBaseIds = new Set((data ?? []).map((knowledgeBase) => knowledgeBase.id));

    setSelectedKnowledgeBaseIds((prev) => {
      if (!prev.length) return prev;

      const next = prev.filter((id) => availableKnowledgeBaseIds.has(id));

      return next.length === prev.length ? prev : next;
    });
  }, [data]);

  useEffect(() => {
    if (!isBulkDeleting) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isBulkDeleting]);

  const handleOpenConfirmModal = (id: number) => {
    openConfirmModal({
      title: t('deleteModal.title'),
      text: (
        <Trans
          t={t}
          i18nKey="deleteModal.text"
          values={{
            knowledgeBase: data?.find((knowledgeBase) => knowledgeBase.id === id)?.title,
            interpolation: { escapeValue: false },
          }}
          components={{ bold: <AntdText strong /> }}
        />
      ),
      onConfirm: () => deleteKnowledgeBase(id),
    });
  };

  const handleToggleKnowledgeBaseSelect = (knowledgeBaseId: number, checked: boolean) => {
    if (isBulkDeleting) return;

    setSelectedKnowledgeBaseIds((prev) => {
      if (checked) {
        if (prev.includes(knowledgeBaseId)) return prev;
        return [...prev, knowledgeBaseId];
      }

      return prev.filter((id) => id !== knowledgeBaseId);
    });
  };

  const handleToggleAllKnowledgeBaseSelect = (checked: boolean) => {
    if (isBulkDeleting) return;

    setSelectedKnowledgeBaseIds(checked ? (data ?? []).map((knowledgeBase) => knowledgeBase.id) : []);
  };

  const [download] = useLazyDownloadKnowledgeBaseFileQuery();

  const handleDownloadKnowledgeBase = async (id: number) => {
    try {
      const result = await download({ knowledge_base_id: id });
      if ('error' in result && result.error) {
        throw result.error;
      }

      const payload = 'data' in result ? result.data : undefined;
      if (!payload) {
        throw new Error('Empty download response');
      }

      const downloadBlob =
        payload instanceof Blob
          ? payload
          : payload.blob instanceof Blob
            ? payload.blob
            : new Blob([payload.blob]);
      const fileName = payload instanceof Blob ? undefined : payload.fileName;
      const url = window.URL.createObjectURL(downloadBlob);
      const a = document.createElement('a');
      a.href = url;

      const fileDate = DateService.format(new Date(), 'DD-MM-YYYY-HH-mm-ss');
      a.download = fileName || `KnowledgeBase_${fileDate}`;

      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error: any) {
      console.error('Error downloading file:', error?.data ?? error);
    }
  };

  const hasKnowledgeBase = (data?.length ?? 0) > 0;
  const selectedKnowledgeBaseCount = selectedKnowledgeBaseIds.length;

  const handleBulkDeleteKnowledgeBase = async () => {
    if (!selectedKnowledgeBaseCount) return;

    const knowledgeBaseIdsToDelete = [...selectedKnowledgeBaseIds];

    setIsBulkDeleting(true);

    try {
      for (const knowledgeBaseId of knowledgeBaseIdsToDelete) {
        await deleteKnowledgeBase(knowledgeBaseId);
      }

      setSelectedKnowledgeBaseIds([]);
    } catch {
      message.error(t('bulkDelete.error'));
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const handleOpenBulkDeleteConfirmModal = () => {
    if (!selectedKnowledgeBaseCount) return;

    openConfirmModal({
      title: t('bulkDelete.title'),
      text: (
        <Flex vertical gap={8}>
          <AntdText>{t('bulkDelete.text', { count: selectedKnowledgeBaseCount })}</AntdText>
          <AntdText type="warning">{t('bulkDelete.warning')}</AntdText>
        </Flex>
      ),
      confirmButtonLabel: t('bulkDelete.action'),
      onConfirm: handleBulkDeleteKnowledgeBase,
    });
  };

  return (
    <Flex vertical gap={20} className={styles.root}>
      <PageHeader title={t('header.title')} />

      <Flex vertical className={styles.container} gap={16}>
        {isLoading && <LoadingState />}
        {!isLoading && isError && <FetchingErrorState refetch={refetch} />}

        {isBulkDeleting && (
          <AntdText type="warning" className={styles.bulkDeleteWarning}>
            {t('bulkDelete.inProgressWarning')}
          </AntdText>
        )}

        {!isLoading && !isError && hasKnowledgeBase && (
          <Flex vertical gap={16}>
            <div className={styles.tableWrapper}>
              <KnowledgeBaseTable
                knowledgeBaseInfo={data}
                selectedKnowledgeBaseIds={selectedKnowledgeBaseIds}
                onToggleKnowledgeBaseSelect={handleToggleKnowledgeBaseSelect}
                onToggleAllKnowledgeBaseSelect={handleToggleAllKnowledgeBaseSelect}
                onDeleteKnowledgeBase={handleOpenConfirmModal}
                onDownloadKnowledgeBase={handleDownloadKnowledgeBase}
                isBulkDeleting={isBulkDeleting}
              />
            </div>

            <Flex align="center" justify="space-between" gap={12} wrap="wrap">
              <Flex align="center" gap={12} wrap="wrap">
                {selectedKnowledgeBaseCount > 0 && (
                  <>
                    <AntdText type="secondary">
                      {t('bulkDelete.selectedCount', { count: selectedKnowledgeBaseCount })}
                    </AntdText>

                    <Button
                      danger
                      size="large"
                      loading={isBulkDeleting}
                      onClick={handleOpenBulkDeleteConfirmModal}
                    >
                      {t('bulkDelete.action')}
                    </Button>
                  </>
                )}
              </Flex>

              <Button
                onClick={openCreateKnowledgeBaseModal}
                type="primary"
                size="large"
                className={styles.button}
                disabled={isBulkDeleting}
              >
                {t('header.addFile')}
              </Button>
            </Flex>
          </Flex>
        )}

        {!isLoading && !isError && !hasKnowledgeBase && <KnowledgeBaseEmptyState />}
      </Flex>

      <KnowledgeBaseCreateModal
        isOpen={isCreateKnowledgeBaseModalOpen}
        onClose={closeCreateKnowledgeBaseModal}
      />
    </Flex>
  );
};
